import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { UploadSimpleIcon, TrashIcon, GlobeIcon } from "@phosphor-icons/react";

const compressImage = (file, maxDim = 900, quality = 0.85) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const SiteAdminPage = () => {
  const { t } = useLang();
  const [form, setForm] = useState({
    company_name: "", tagline: "", logo: null, hero_image: null,
    phone: "", whatsapp: "", email: "", address: "",
    facebook: "", instagram: "", twitter: "", about: "",
  });
  const [saving, setSaving] = useState(false);
  const logoRef = useRef(null);
  const heroRef = useRef(null);

  useEffect(() => {
    api.get("/admin/site").then(({ data }) => {
      setForm((f) => ({ ...f, ...data }));
    }).catch(() => {});
  }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleImage = async (e, field, maxDim) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 6_000_000) { toast.error("Image too large"); return; }
    const dataUrl = await compressImage(file, maxDim);
    if (dataUrl.length > 900_000) { toast.error("Image too large after compress"); return; }
    setField(field, dataUrl);
    if (e.target) e.target.value = "";
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/admin/site", form);
      toast.success(t("savedSettings"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 sm:space-y-8 max-w-4xl" data-testid="site-admin-page">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">/website</div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">إدارة الموقع الإلكتروني</h1>
        <p className="text-sm text-zinc-500 mt-2">هذه الإعدادات تظهر على الصفحة العامة <code className="text-zinc-300">/site</code> لكل الزوار.</p>
      </div>

      <form onSubmit={save} className="space-y-6">
        <Section title="الهوية" desc="اسم الشركة وشعارها وصورة الغلاف">
          <Row2>
            <Field label="اسم الشركة"><Input data-testid="site-company-name" value={form.company_name || ""} onChange={(e) => setField("company_name", e.target.value)} className="bg-[#0A0A0A] border-white/10" /></Field>
            <Field label="الشعار المختصر (Tagline)"><Input data-testid="site-tagline" value={form.tagline || ""} onChange={(e) => setField("tagline", e.target.value)} className="bg-[#0A0A0A] border-white/10" /></Field>
          </Row2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
            <ImagePicker label="شعار المكتب (Logo)" value={form.logo} onPick={(f) => handleImage(f, "logo", 400)} onClear={() => setField("logo", null)} inputRef={logoRef} aspect="aspect-square" testid="site-logo" />
            <ImagePicker label="صورة الغلاف (Hero)" value={form.hero_image} onPick={(f) => handleImage(f, "hero_image", 1400)} onClear={() => setField("hero_image", null)} inputRef={heroRef} aspect="aspect-[16/9]" testid="site-hero" />
          </div>
        </Section>

        <Section title="التواصل" desc="أرقام الهاتف وواتساب والبريد والعنوان">
          <Row2>
            <Field label="رقم الهاتف"><Input dir="ltr" data-testid="site-phone" value={form.phone || ""} onChange={(e) => setField("phone", e.target.value)} className="bg-[#0A0A0A] border-white/10 font-mono" placeholder="+96893803727" /></Field>
            <Field label="رقم واتساب"><Input dir="ltr" data-testid="site-whatsapp" value={form.whatsapp || ""} onChange={(e) => setField("whatsapp", e.target.value)} className="bg-[#0A0A0A] border-white/10 font-mono" placeholder="+96893803727" /></Field>
            <Field label="البريد الإلكتروني"><Input dir="ltr" data-testid="site-email" value={form.email || ""} onChange={(e) => setField("email", e.target.value)} className="bg-[#0A0A0A] border-white/10" placeholder="info@abushaikha.com" /></Field>
            <Field label="العنوان"><Input data-testid="site-address" value={form.address || ""} onChange={(e) => setField("address", e.target.value)} className="bg-[#0A0A0A] border-white/10" /></Field>
          </Row2>
        </Section>

        <Section title="شبكات التواصل" desc="روابط صفحاتك الرسمية">
          <Row2>
            <Field label="Facebook URL"><Input dir="ltr" data-testid="site-facebook" value={form.facebook || ""} onChange={(e) => setField("facebook", e.target.value)} className="bg-[#0A0A0A] border-white/10" placeholder="facebook.com/yourpage" /></Field>
            <Field label="Instagram URL"><Input dir="ltr" data-testid="site-instagram" value={form.instagram || ""} onChange={(e) => setField("instagram", e.target.value)} className="bg-[#0A0A0A] border-white/10" placeholder="instagram.com/yourpage" /></Field>
            <Field label="X (Twitter) URL"><Input dir="ltr" data-testid="site-twitter" value={form.twitter || ""} onChange={(e) => setField("twitter", e.target.value)} className="bg-[#0A0A0A] border-white/10" placeholder="x.com/yourpage" /></Field>
          </Row2>
        </Section>

        <Section title="نبذة" desc="نص تعريفي بالشركة (يظهر في قسم عن الشركة)">
          <Textarea data-testid="site-about" value={form.about || ""} onChange={(e) => setField("about", e.target.value)} rows={5} className="bg-[#0A0A0A] border-white/10" />
        </Section>

        <div className="flex justify-end gap-3 flex-wrap">
          <a href="/site" target="_blank" rel="noreferrer" data-testid="site-preview-link" className="inline-flex items-center gap-2 border border-white/10 bg-[#0A0A0A] hover:bg-[#1C1C1C] px-4 py-2 text-sm">
            <GlobeIcon size={14} weight="bold" /> معاينة الموقع
          </a>
          <button type="submit" disabled={saving} data-testid="site-save-btn" style={{ backgroundColor: "var(--primary-action, #007AFF)", color: "#0A0A0A" }} className="px-6 py-3 text-sm font-semibold hover:opacity-90 disabled:opacity-60">
            حفظ
          </button>
        </div>
      </form>
    </div>
  );
};

const Section = ({ title, desc, children }) => (
  <section className="border border-white/10 bg-[#121212]">
    <div className="px-4 sm:px-6 py-4 border-b border-white/10">
      <h2 className="text-lg font-semibold">{title}</h2>
      {desc && <p className="text-xs text-zinc-500 mt-0.5">{desc}</p>}
    </div>
    <div className="p-4 sm:p-6 space-y-4">{children}</div>
  </section>
);

const Row2 = ({ children }) => <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">{label}</Label>
    {children}
  </div>
);

const ImagePicker = ({ label, value, onPick, onClear, inputRef, aspect, testid }) => (
  <div className="space-y-2">
    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">{label}</Label>
    <div className={`w-full ${aspect} border border-white/10 bg-[#0A0A0A] overflow-hidden`}>
      {value ? <img src={value} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-xs text-zinc-600 uppercase tracking-[0.2em]">—</div>}
    </div>
    <div className="flex gap-2">
      <input type="file" accept="image/*" ref={inputRef} onChange={onPick} className="hidden" data-testid={`${testid}-file`} />
      <button type="button" onClick={() => inputRef.current?.click()} data-testid={`${testid}-upload`} className="inline-flex items-center gap-1.5 border border-white/10 bg-[#0A0A0A] hover:bg-[#1C1C1C] px-3 py-1.5 text-xs"><UploadSimpleIcon size={12} weight="bold" /> رفع</button>
      {value && <button type="button" onClick={onClear} data-testid={`${testid}-clear`} className="inline-flex items-center gap-1.5 border border-white/10 bg-[#0A0A0A] hover:bg-[#1C1C1C] px-3 py-1.5 text-xs text-[#FF3B30]"><TrashIcon size={12} weight="bold" /> حذف</button>}
    </div>
  </div>
);
